// ============================================================================
// CreateVoiceRoomDialog.tsx — создание голосовой комнаты (волна 2, D7).
//
// Создатель сразу становится участником (moderator, active) — сервер
// (CreateRoom). Приватная комната доступна только пользователям с выбранными
// ролями (settings.role_ids — ИМЕНА ролей, как в JWT и section_roles).
// ============================================================================

import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import type { Role } from '@zdc/sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { getSDK } from '@/store/sdkStore';
import { useToastStore } from '@/store/toastStore';

interface CreateVoiceRoomDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (roomId: string) => void;
}

export function CreateVoiceRoomDialog({ open, onClose, onCreated }: CreateVoiceRoomDialogProps) {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [roles, setRoles] = useState<Role[]>([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [rolesError, setRolesError] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const showError = (m: string) => useToastStore.getState().showError(m);
  const showSuccess = (m: string) => useToastStore.getState().showSuccess(m);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const r = await getSDK().auth.getRoles({});
        if (!cancelled) setRoles([...r.items]);
      } catch (e) {
        if (cancelled) return;
        logErrorToJournal(e, 'Голосовые комнаты: загрузка ролей для приватной комнаты');
        setRolesError('Роли недоступны — приватная комната будет доступна только вам и админам');
      } finally {
        if (!cancelled) setRolesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setName('');
    setTopic('');
    setIsPublic(true);
    setRoleIds([]);
    setError('');
    setRolesError('');
    setRolesLoaded(false);
  }, [open]);

  const toggleRole = (roleName: string) => {
    setRoleIds((prev) =>
      prev.includes(roleName) ? prev.filter((r) => r !== roleName) : [...prev, roleName],
    );
  };

  const submit = async () => {
    const roomName = name.trim();
    if (!roomName) {
      setError('Укажите имя комнаты');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const room = await getSDK().voiceRooms.createRoom({
        name: roomName,
        topic: topic.trim(),
        isPersistent: true,
        settings: {
          maxParticipants: 0,
          isPublic,
          roleIds: isPublic ? [] : roleIds,
        },
      });
      showSuccess('Комната создана — вы в ней');
      onCreated(room.id);
    } catch (e) {
      logErrorToJournal(e, 'Голосовые комнаты: создание комнаты');
      setError(friendlyError(e));
      showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <UniversalWindow
      open={open}
      onClose={onClose}
      title="Новая голосовая комната"
      width="w-[min(520px,94vw)]"
      height="auto"
      resizable={false}
    >
      <div className="p-3 space-y-3">
        <div className="space-y-1">
          <label className="text-xs font-medium">Имя комнаты *</label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например, Переговорная"
            disabled={busy}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submit();
              }
            }}
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">Тема</label>
          <Input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="О чём комната (необязательно)"
            disabled={busy}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isPublic}
            onChange={(e) => setIsPublic(e.target.checked)}
            disabled={busy}
          />
          Публичная — вход всем авторизованным
        </label>
        {!isPublic && (
          <div className="space-y-1">
            <div className="text-xs font-medium">Доступ по ролям</div>
            {rolesError ? (
              <div className="text-xs text-amber-600 dark:text-amber-400">{rolesError}</div>
            ) : !rolesLoaded ? (
              <div className="text-xs text-muted-foreground">Загрузка ролей…</div>
            ) : roles.length === 0 ? (
              <div className="text-xs text-muted-foreground">
                В базе нет ролей — приватная комната будет доступна только вам и админам
              </div>
            ) : (
              <div className="flex flex-wrap gap-1">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => toggleRole(role.name)}
                    disabled={busy}
                    className={`rounded-full border px-2 py-0.5 text-xs ${
                      roleIds.includes(role.name)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground'
                    }`}
                    title={role.description || role.name}
                  >
                    {role.name}
                  </button>
                ))}
              </div>
            )}
            {!rolesError && rolesLoaded && roles.length > 0 && (
              <div className="text-xs text-muted-foreground">
                {roleIds.length === 0
                  ? 'Роли не выбраны — комната будет доступна только создателю и админам'
                  : 'Вход — пользователям с любой из выбранных ролей'}
              </div>
            )}
          </div>
        )}
        {error && <div className="text-xs text-destructive">{error}</div>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button size="sm" onClick={() => void submit()} disabled={busy || !name.trim()}>
            <Plus className="h-4 w-4 mr-1" /> {busy ? 'Создаём…' : 'Создать'}
          </Button>
        </div>
      </div>
    </UniversalWindow>
  );
}
